import React from 'react'
import { useFormContext } from 'react-hook-form'
import styled from '@emotion/styled'
import InputField from 'modules/ui/molecules/FormComponents/InputField'
import SelectField from 'modules/ui/molecules/FormComponents/SelectField'
import Column from 'modules/ui/atoms/Layout/Column'
import Row from 'modules/ui/atoms/Layout/Row'
import { useTranslation } from 'next-i18next'
import { STRIPE_COUNTRY_CODES } from 'utils/constants/misc'
import { DonorInfoFormData } from './donor-info-fields'
import TextPlaceloader from 'modules/ui/atoms/Loaders/TextPlaceloader'
import Placeloader from 'modules/ui/atoms/Loaders/Placeloader'

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`

// Skeleton component for loading state
const AddressFieldsSkeleton = () => (
  <FieldsContainer>
    <Row>
      <Column>
        <TextPlaceloader w={68} fs="14px" lh="20px" />
        <Placeloader style={{ width: '100%', height: 45, borderRadius: 8 }} />
      </Column>
      <Column>
        <TextPlaceloader w={68} fs="14px" lh="20px" />
        <Placeloader style={{ width: '100%', height: 45, borderRadius: 8 }} />
      </Column>
    </Row>
  </FieldsContainer>
)

type AddressFieldsProps = {
  isLoading?: boolean
}

const AddressFields = ({ isLoading = false }: AddressFieldsProps) => {
  const { t } = useTranslation()
  const form = useFormContext<DonorInfoFormData>()

  const {
    formState: { errors },
    watch,
  } = form

  const watchCountry = watch('country') || 'US'

  // Convert country codes to options for the select field
  const countryOptions = STRIPE_COUNTRY_CODES.map((country) => ({
    value: country.code,
    label: country.country,
  }))

  const fieldError = (fieldName: keyof DonorInfoFormData) =>
    errors[fieldName] as { message: string } | undefined

  if (isLoading) {
    return <AddressFieldsSkeleton />
  }

  return (
    <FieldsContainer>
      {/* Country and Zip Code - Side by side */}
      <Row>
        <Column>
          <SelectField
            required
            id="country"
            label={t('AddressFields.country.label', 'Country')}
            error={fieldError('country')}
            options={countryOptions}
            autoComplete="country"
            dataId="donor-info-country-select"
            value={watchCountry}
          />
        </Column>
        <Column>
          <InputField
            required
            id="zipCode"
            label={
              watchCountry === 'US'
                ? t('AddressFields.zipCode.label', 'Zip code')
                : t('AddressFields.postalCode.label', 'Postal code')
            }
            error={fieldError('zipCode')}
            autoComplete="postal-code"
            dataId="donor-info-zip-code-input"
          />
        </Column>
      </Row>
    </FieldsContainer>
  )
}

export default AddressFields
